import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="conversation-pdf-export"
export default class extends Controller {
  static values = {
    generateUrl: String,
    statusUrl: String
  }

  connect() {
    this.pollingInterval = null
    this.originalButtonText = null
  }

  disconnect() {
    this.stopPolling()
  }

  async export(event) {
    event.preventDefault()

    const button = event.currentTarget
    if (button.disabled) return

    this.disableButton(button)

    try {
      // Make POST request to generate PDF
      const response = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Accept': 'application/pdf',
          'X-CSRF-Token': this.getCsrfToken()
        }
      })

      if (!response.ok) {
        throw new Error('Failed to generate PDF')
      }

      // Get the PDF blob
      const blob = await response.blob()

      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      // Get filename from Content-Disposition header or use default
      const disposition = response.headers.get('Content-Disposition')
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : 'conversation.pdf'

      a.download = filename
      document.body.appendChild(a)
      a.click()

      // Cleanup
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      this.showSuccess('PDF downloaded!')
      this.enableButton(button)
    } catch (error) {
      this.showError('Failed to export PDF. Please try again.')
      this.enableButton(button)
    }
  }

  startPolling(button) {
    let attempts = 0
    const maxAttempts = 60 // 2 minutes max

    this.pollingInterval = setInterval(async () => {
      attempts++

      try {
        const response = await fetch(this.statusUrlValue, {
          headers: { 'Accept': 'application/json' }
        })
        const data = await response.json()

        if (data.status === 'completed' && data.download_url) {
          this.stopPolling()
          window.location.href = data.download_url
          this.showSuccess('PDF downloaded!')
          this.enableButton(button)
        } else if (data.status === 'error') {
          this.stopPolling()
          this.showError(data.error || 'PDF generation failed')
          this.enableButton(button)
        } else if (attempts >= maxAttempts) {
          this.stopPolling()
          this.showError('PDF generation is taking longer than expected. Please try again later.')
          this.enableButton(button)
        }
      } catch (error) {
        this.stopPolling()
        this.showError('Failed to check PDF status')
        this.enableButton(button)
      }
    }, 2000) // Poll every 2 seconds
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  showSuccess(message) {
    const event = new CustomEvent('toast:show', {
      detail: {
        message: message,
        type: 'success'
      }
    })
    window.dispatchEvent(event)
  }

  showError(message) {
    const event = new CustomEvent('toast:show', {
      detail: {
        message: message,
        type: 'error'
      }
    })
    window.dispatchEvent(event)
  }

  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  disableButton(button) {
    button.disabled = true

    // Find all spans and get the last one (text span, not icon span)
    const spans = button.querySelectorAll('span')
    const textSpan = spans[spans.length - 1]
    if (textSpan) {
      this.originalButtonText = textSpan.textContent.trim()
      textSpan.textContent = 'Generating...'
    }
  }

  enableButton(button) {
    button.disabled = false

    // Restore original button text
    const spans = button.querySelectorAll('span')
    const textSpan = spans[spans.length - 1]
    if (textSpan && this.originalButtonText) {
      textSpan.textContent = this.originalButtonText
      this.originalButtonText = null
    }
  }
}
